export const parseToDecimal = (str: string): number => {
  if (!str) return 0; // Return 0 if the string is empty or null
  const num = parseFloat(str);
  return parseFloat(num.toFixed(2));
};

export function formatNumberWithCommas(number: number | string) {
  if (!number || typeof number !== "string" || number === "-") return number;
  return Number(number).toLocaleString("en-US", { maximumFractionDigits: 20 });
}

export function RoundDecimalToInteger(number: number | string) {
  // console.log(number);
  if (!number || number == "-") return number;

  if (typeof number === "string") {
    const num = number.replaceAll(",", ""); // Remove commas

    return formatNumberWithCommas(`${Math.round(Number(num))}`); // Convert string to number before rounding
  }
  return formatNumberWithCommas(`${Math.round(number)}`);
}

function cleanNumber(numStr: string) {
  if (!numStr || typeof numStr !== "string") return numStr;
  return numStr
    .replace(/^(-?)0+(\d)/, "$1$2")
    .replace(/(\.\d*?)0+$/, "$1")
    .replace(/\.$/, "");
}

export function formatNumberWithFillZero(value: number | string | null | undefined, precision: number = 4): string {
  // console.log("CURRENT VaL :: ", value);
  if (value && `${value}` !== "-") {
    const formattedValue = value.toString().replaceAll(",", "");
    // console.log("FORMAT VAL : ", formattedValue);
    const [integerPart, decimalPart = ""] = formattedValue.split(".");

    // Format the integer part with comma separators
    const formattedInteger = parseInt(integerPart).toLocaleString("en-US");

    if (decimalPart) {
      // Ensure the decimal part has exactly 'precision' digits by filling with zeros
      const limitedDecimal = decimalPart.padEnd(precision, "0").slice(0, precision);
      return limitedDecimal ? `${formattedInteger}.${limitedDecimal}` : formattedInteger;
    } else {
      return `${formattedInteger}.${"0".repeat(precision)}`;
    }
  } else if (`${value}` === "-") {
    return `${value}`;
  } else {
    return "0.00";
  }
}

export function formatNumber(value: number | null | undefined, precision: number = 4): string {
  if (value && `${value}` !== "-") {
    const formattedValue = value.toString();
    const [integerPart, decimalPart] = formattedValue.split(".");
    // Format the integer part with comma separators
    const formattedInteger = parseInt(integerPart).toLocaleString("en-US");
    // Combine integer and decimal parts, ensuring the decimal part has at most 4 digits
    if (decimalPart) {
      // Limit the decimal part to 4 (precision) digits
      const limitedDecimal = decimalPart.slice(0, precision);
      return cleanNumber(`${formattedInteger}.${limitedDecimal}`);
    } else {
      return formattedInteger; // No decimal part
    }
  } else if (`${value}` == "-") {
    return `${value}`;
  } else {
    return "0";
  }
}

export function convertStringToRawNumber(value: string | number) {
  if (typeof value !== "string" || typeof value !== "number") return value;
  return `${value}`.replaceAll(",", "");
}

export function parseToFixedDecimalWithComma(value: number, precision: number): string {
  if (value) {
    if (value.toString() === "-") return value.toString();
    // Fix the number to the given precision and split it into integer and decimal parts
    const formattedValue = value.toFixed(precision);
    const [integerPart, decimalPart] = formattedValue.split(".");
    // console.log("Details : ", { value, decimalPart });
    // Format the integer part with comma separators
    const formattedInteger = parseInt(integerPart, 10).toLocaleString("en-US");

    // Return the formatted integer part along with the decimal part, if it exists
    return decimalPart ? `${formattedInteger}.${decimalPart}` : formattedInteger;
  } else {
    return Number("0").toFixed(precision);
  }
}

export function formatNumberWithRoundUpDecimal(value: number | null | undefined, precision: number = 4): string {
  if (value) {
    const formattedValue = value.toString();
    const [integerPart, decimalPart] = formattedValue.split(".");
    // Format the integer part with comma separators
    const formattedInteger = parseInt(integerPart).toLocaleString("en-US");
    // Combine integer and decimal parts, ensuring the decimal part has at most 4 digits
    if (decimalPart) {
      // Limit the decimal part to 4 (precision) digits
      const limitedDecimal = parseFloat(`${value}`).toFixed(precision).split(".")[1];
      return cleanNumber(`${formattedInteger}.${limitedDecimal}`);
    } else {
      return formattedInteger; // No decimal part
    }
  } else {
    return "0";
  }
}

export function parseAndFormatScientificNumber(value: string, maxDigit: number = 4, minDigit: number = 0) {
  if (value) {
    const numberValue = Number(value);

    const formatter = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: minDigit,
      maximumFractionDigits: maxDigit,
    });

    return formatter.format(numberValue);
  } else if (value == "-") return value;
  else {
    return 0;
  }
}

export function parseAndFormatScientificNumberWithoutZeroReturn(value: string, maxDigit: number = 4, minDigit: number = 0) {
  if (!value || value == "-") return value;

  const numberValue = Number(value);

  const formatter = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: minDigit,
    maximumFractionDigits: maxDigit,
  });

  return formatter.format(numberValue);
}

export function formatOnlyComma(value: string | number) {
  if (value) {
    const numberValue = Number(value);
    const formatter = new Intl.NumberFormat("en-US", {});
    return formatter.format(numberValue);
  } else if (value == "-") return value;
  else {
    return 0;
  }
}

export function ParserNumber(value: string | undefined): number {
  if (!value) return 0;
  return parseFloat(value.replace(/,/g, "")) || 0;
}

export function cleanNumberRemoveComma(numStr: string) {
  if (!numStr || typeof numStr !== "string") return numStr;
  return numStr
    .replace(/^(-?)0+(\d)/, "$1$2")
    .replace(/(\.\d*?)0+$/, "$1")
    .replace(/\.$/, "")
    .replace(/,/g, "");
}

export function cleanNumberStringInObject(data: Record<string, string | number>, focus: string[], type: string = "number") {
  for (const key in data) {
    if (focus.includes(key)) {
      const clean = cleanNumberRemoveComma(`${data[key]}`);
      data[key] = type === "number" ? Number(clean) : clean;
    }
  }
  return data;
}
export function fillZeroByKeyList(data: any, hashMap: Record<string, number>) {
  for (const key in hashMap) {
    data[key] = formatNumberWithFillZero(data[key], hashMap[key]);
  }
  return data;
}

export function parseAndFormatScientificNumberRemoveComma(value: string, maxDigit: number = 4, minDigit: number = 0) {
  if (value) {
    const numberValue = Number(value);

    const formatter = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: minDigit,
      maximumFractionDigits: maxDigit,
    });

    return formatter.format(numberValue).replaceAll(",", "");
  } else if (value == "-") return value;
  else {
    return 0;
  }
}
